import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { SIDEMENU } from "../../../databucket";
import { Card, Radio, Checkbox, Avatar, Tag, Typography, Space, Badge, Row, Col, message, Spin } from "antd";
import { SearchOutlined, ApartmentOutlined, UserOutlined, SaveOutlined, } from "@ant-design/icons";
import { InputField, SelectInput, AntdButton } from "../../../blueplanitUtils/InputField";
import { fetchAllDepartmentAccess, fetchAllEmployeeAccess, upsertDepartmentAccess, upsertEmployeeAccess, } from "../../../Slices/Employeeaccessslice";

const { Title, Text } = Typography;

const MODE = { DEPARTMENT: "department", INDIVIDUAL: "individual" };
const ROUTE_FILTER = { ALL: "all", INHERITED: "inherited", CUSTOM: "custom" };
const ROUTE_TYPE = { DEPARTMENT: "department", INHERITED: "inherited", CUSTOM: "custom", NONE: "none" };

const normalizeRoutePath = (path) => path && (path.startsWith('/') ? path : `/${path}`);
const getSubRoutePaths = (menu) => menu.Subfields?.map(sub => normalizeRoutePath(sub.name)) || [];
const extractDepartments = (nestedDatabucket) => nestedDatabucket[0]?.children[1]?.children?.map(dept => ({ name: dept.name })) || [];
const filterActiveEmployees = (allEmployees) => allEmployees?.filter(emp => emp.employeestatus === "Active" && emp.department?.trim()) || [];
const formatEmployeeName = (emp) => `${emp?.firstname} ${emp?.lastname}`;

// Main component
export default function AccessManagement() {
  const dispatch = useDispatch();
  const [messageApi, contextHolder] = message.useMessage();

  const allEmployees = useSelector((state) => state.employee.AllEmployees);
  const NestedDatabucket = useSelector((state) => state.dataBucket.NestedDatabucket);
  const currentUser = useSelector((state) => state?.auth?.userData);
  const { allDepartmentAccess, allEmployeeAccess, loading, saving } = useSelector((state) => state.employeeAccess);

  const { departments, employees, activeDepartments } = useMemo(() => {
    const depts = extractDepartments(NestedDatabucket);
    console.log((
      "depts", NestedDatabucket
    ))
    const emps = filterActiveEmployees(allEmployees);
    return {
      departments: depts,
      employees: emps,
      activeDepartments: depts.map(dept => ({
        ...dept,
        employeeCount: emps.filter(e => e.department === dept.name).length,
      }))
    };
  }, [NestedDatabucket, allEmployees]);

  const [mode, setMode] = useState(MODE.DEPARTMENT);
  const [selectedDepartment, setSelectedDepartment] = useState(departments[0]?.name || "Operations");
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [searchRoutes, setSearchRoutes] = useState("");
  const [searchDepartments, setSearchDepartments] = useState("");
  const [routeFilter, setRouteFilter] = useState(ROUTE_FILTER.ALL);
  const [departmentRoutes, setDepartmentRoutes] = useState({});
  const [employeeCustomRoutes, setEmployeeCustomRoutes] = useState({});

  // Load data on mount
  useEffect(() => {
    Promise.all([
      dispatch(fetchAllDepartmentAccess({ includeInactive: false })).unwrap(),
      dispatch(fetchAllEmployeeAccess({ includeInactive: false })).unwrap()
    ]).then(([deptData, empData]) => {
      if (deptData?.length) {
        const deptRoutesMap = {};
        deptData.forEach(dept => {
          deptRoutesMap[dept.department] = (dept.routes || []).map(normalizeRoutePath).filter(Boolean);
        });
        setDepartmentRoutes(deptRoutesMap);
      }
      if (empData?.length) {
        const empRoutesMap = {};
        empData.forEach(emp => {
          empRoutesMap[emp.employeeId] = (emp.routes || []).map(normalizeRoutePath).filter(Boolean);
        });
        setEmployeeCustomRoutes(empRoutesMap);
      }
    }).catch(error => {
      console.error('Error loading access data:', error);
      messageApi.error(error?.message || error?.toString?.() || 'Failed to load access data');
    });
  }, [dispatch, messageApi]);


  const { departmentEmployees, currentEmployee, currentDepartment, filteredDepartments } = useMemo(() => ({
    departmentEmployees: employees.filter(e => e.department === selectedDepartment),
    currentEmployee: employees.find(e => e.id === selectedEmployee),
    currentDepartment: activeDepartments.find(d => d.name === selectedDepartment),
    filteredDepartments: !searchDepartments ? activeDepartments : activeDepartments.filter(d => d.name.toLowerCase().includes(searchDepartments.toLowerCase()))
  }), [employees, selectedDepartment, selectedEmployee, activeDepartments, searchDepartments]);
  

  useEffect(() => {
    if (!departmentEmployees.length) return;

    const exists = departmentEmployees.some(
      emp => emp.id === selectedEmployee
    );

    // set default ONLY if current employee is invalid
    if (!exists) {
      setSelectedEmployee(departmentEmployees[0].id);
    }
  }, [departmentEmployees, selectedEmployee]);



  const getInheritedRoutes = useCallback((employeeId) => {
    const emp = employees.find(e => e.id === employeeId);
    return emp ? departmentRoutes[emp.department] || [] : [];
  }, [employees, departmentRoutes]);

  const getAllEmployeeRoutes = useCallback((employeeId) => {
    const inherited = getInheritedRoutes(employeeId);
    const custom = employeeCustomRoutes[employeeId] || [];
    return [...new Set([...inherited, ...custom])];
  }, [getInheritedRoutes, employeeCustomRoutes]);

  const getRouteStatus = useCallback((menu, subfield = null) => {
    if (subfield) {
      const routePath = normalizeRoutePath(subfield.name);

      if (mode === MODE.DEPARTMENT) {
        const routes = departmentRoutes[selectedDepartment] || [];
        return { checked: routes.includes(routePath), type: ROUTE_TYPE.DEPARTMENT };
      }

      if (!selectedEmployee) return { checked: false, type: ROUTE_TYPE.NONE };

      const inherited = getInheritedRoutes(selectedEmployee);
      const custom = employeeCustomRoutes[selectedEmployee] || [];

      if (inherited.includes(routePath)) return { checked: true, type: ROUTE_TYPE.INHERITED };
      if (custom.includes(routePath)) return { checked: true, type: ROUTE_TYPE.CUSTOM };
      return { checked: false, type: ROUTE_TYPE.NONE };
    } else {
      const subRoutePaths = getSubRoutePaths(menu);
      if (subRoutePaths.length === 0) return { checked: false, type: ROUTE_TYPE.NONE };

      if (mode === MODE.DEPARTMENT) {
        const routes = departmentRoutes[selectedDepartment] || [];
        return { checked: subRoutePaths.every(path => routes.includes(path)), type: ROUTE_TYPE.DEPARTMENT };
      }

      if (!selectedEmployee) return { checked: false, type: ROUTE_TYPE.NONE };

      const allRoutes = getAllEmployeeRoutes(selectedEmployee);
      const allChecked = subRoutePaths.every(path => allRoutes.includes(path));
      const inherited = getInheritedRoutes(selectedEmployee);
      const custom = employeeCustomRoutes[selectedEmployee] || [];
      const hasInherited = subRoutePaths.some(path => inherited.includes(path));
      const hasCustom = subRoutePaths.some(path => custom.includes(path));

      let type = ROUTE_TYPE.NONE;
      if (allChecked) {
        type = hasInherited ? ROUTE_TYPE.INHERITED : hasCustom ? ROUTE_TYPE.CUSTOM : ROUTE_TYPE.NONE;
      }

      return { checked: allChecked, type };
    }
  }, [mode, selectedDepartment, selectedEmployee, departmentRoutes, employeeCustomRoutes, getInheritedRoutes, getAllEmployeeRoutes]);

  const filteredRoutes = SIDEMENU.filter(menu => {
    if (!searchRoutes) return true;
    const lowerSearch = searchRoutes.toLowerCase();
    return menu.name.toLowerCase().includes(lowerSearch) ||
      menu.Subfields?.some(sub => sub.displayName.toLowerCase().includes(lowerSearch));
  }).filter(menu => {
    if (mode !== MODE.INDIVIDUAL || routeFilter === ROUTE_FILTER.ALL || !selectedEmployee) return true;
    const emp = employees.find(e => e.id === selectedEmployee);
    if (!emp) return true;
    const inherited = departmentRoutes[emp.department] || [];
    const custom = employeeCustomRoutes[selectedEmployee] || [];
    const subPaths = getSubRoutePaths(menu);

    if (routeFilter === ROUTE_FILTER.INHERITED) return subPaths.some(path => inherited.includes(path));
    if (routeFilter === ROUTE_FILTER.CUSTOM) return subPaths.some(path => custom.includes(path));
    return true;
  });

  const assignedCount = mode === MODE.DEPARTMENT ?
    (departmentRoutes[selectedDepartment] || []).length :
    selectedEmployee ? getAllEmployeeRoutes(selectedEmployee).length : 0;

  const handleDepartmentRouteToggle = useCallback((menu, subfield = null) => {
    const routePaths = subfield ? [normalizeRoutePath(subfield.name)] : getSubRoutePaths(menu);
    if (!routePaths.length) return;

    setDepartmentRoutes(prev => {
      const current = prev[selectedDepartment] || [];
      const allSelected = routePaths.every(path => current.includes(path));
      const updated = allSelected ?
        current.filter(path => !routePaths.includes(path)) :
        [...new Set([...current, ...routePaths])];
      return { ...prev, [selectedDepartment]: updated };
    });
  }, [selectedDepartment]);

  const handleEmployeeRouteToggle = useCallback((menu, subfield = null) => {
    if (!selectedEmployee) return;

    const routePaths = subfield ? [normalizeRoutePath(subfield.name)] : getSubRoutePaths(menu);
    if (!routePaths.length) return;

    const inherited = getInheritedRoutes(selectedEmployee);
    const hasInherited = routePaths.some(path => inherited.includes(path));

    if (hasInherited) {
      messageApi.info("Some routes are inherited from department settings and cannot be toggled");
      return;
    }

    setEmployeeCustomRoutes(prev => {
      const current = prev[selectedEmployee] || [];
      const allSelected = routePaths.every(path => current.includes(path));
      const updated = allSelected ?
        current.filter(path => !routePaths.includes(path)) :
        [...new Set([...current, ...routePaths])];
      return { ...prev, [selectedEmployee]: updated };
    });
  }, [selectedEmployee, getInheritedRoutes, messageApi]);

  const getChangedRoutes = useCallback((original, current) => ({
    routesToAdd: current.filter(route => !original.includes(route)),
    routesToRemove: original.filter(route => !current.includes(route))
  }), []);

  const handleSave = useCallback(async () => {
    try {
      const userData = currentUser?.email || currentUser?.username || "admin";

      if (mode === MODE.DEPARTMENT) {
        const reduxDept = allDepartmentAccess?.find(d => d.department === selectedDepartment);
        const originalRoutes = (reduxDept?.routes || []).map(normalizeRoutePath).filter(Boolean);
        const { routesToAdd, routesToRemove } = getChangedRoutes(originalRoutes, departmentRoutes[selectedDepartment] || []);

        if (!routesToAdd.length && !routesToRemove.length) {
          messageApi.info("No changes to save");
          return;
        }

        await dispatch(upsertDepartmentAccess({
          department: selectedDepartment,
          routesToAdd,
          routesToRemove,
          changedBy: userData,
          note: `Updated routes for ${selectedDepartment} department`
        })).unwrap();

        messageApi.success(`Changes saved for ${selectedDepartment} department`);
      } else {
        const reduxEmp = allEmployeeAccess?.find(e => e.employeeId === selectedEmployee);
        const originalRoutes = (reduxEmp?.routes || []).map(normalizeRoutePath).filter(Boolean);
        const { routesToAdd, routesToRemove } = getChangedRoutes(originalRoutes, employeeCustomRoutes[selectedEmployee] || []);

        if (!routesToAdd.length && !routesToRemove.length) {
          messageApi.info("No changes to save");
          return;
        }

        const empName = formatEmployeeName(currentEmployee);
        await dispatch(upsertEmployeeAccess({
          employeeId: selectedEmployee,
          employeeName: empName,
          employeeDepartment: currentEmployee?.department,
          routesToAdd,
          routesToRemove,
          changedBy: userData,
          note: `Updated custom routes for ${empName}`
        })).unwrap();

        messageApi.success(`Changes saved for ${empName}`);
      }
    } catch (error) {
      console.error('Save error:', error);
      messageApi.error(error?.message || error?.toString?.() || "Failed to save changes");
    }
  }, [mode, selectedDepartment, selectedEmployee, currentEmployee, currentUser, departmentRoutes, employeeCustomRoutes, allDepartmentAccess, allEmployeeAccess, dispatch, messageApi, getChangedRoutes]);

  const hasUnsavedChanges = useMemo(() => {
    if (mode === MODE.DEPARTMENT) {
      const reduxDept = allDepartmentAccess?.find(d => d.department === selectedDepartment);
      const originalRoutes = (reduxDept?.routes || []).map(normalizeRoutePath).filter(Boolean);
      const currentRoutes = departmentRoutes[selectedDepartment] || [];
      return JSON.stringify([...originalRoutes].sort()) !== JSON.stringify([...currentRoutes].sort());
    }
    const reduxEmp = allEmployeeAccess?.find(e => e.employeeId === selectedEmployee);
    const originalRoutes = (reduxEmp?.routes || []).map(normalizeRoutePath).filter(Boolean);
    const currentRoutes = employeeCustomRoutes[selectedEmployee] || [];
    return JSON.stringify([...originalRoutes].sort()) !== JSON.stringify([...currentRoutes].sort());
  }, [mode, selectedDepartment, selectedEmployee, departmentRoutes, employeeCustomRoutes, allDepartmentAccess, allEmployeeAccess]);

  if (loading) {
    return (
      <div style={{ ...styles.container, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <Spin size="large" tip="Loading access data..." />
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {contextHolder}

      <div style={styles.headerRow}>
        <div>
          <Title level={3} style={{ margin: 0, marginBottom: 4 }}>
            Access Management
          </Title>
          <Text type="secondary">
            Manage route permissions by department or individual employee
          </Text>
        </div>
        <Radio.Group value={mode} onChange={(e) => setMode(e.target.value)} buttonStyle="solid" size="medium">
          <Radio.Button value={MODE.DEPARTMENT}>
            <ApartmentOutlined style={{ marginRight: 8 }} />
            Department Access
          </Radio.Button>
          <Radio.Button value={MODE.INDIVIDUAL}>
            <UserOutlined style={{ marginRight: 8 }} />
            Individual Employee
          </Radio.Button>
        </Radio.Group>
      </div>

      {mode === MODE.INDIVIDUAL && (
        <Card style={{ ...styles.mainCard, marginBottom: 20 }}>
          <Title level={5} style={{ marginBottom: 16 }}>
            Select Department & Employee
          </Title>
          <Row gutter={24}>
            <Col span={12}>
              <SelectInput
                label="Department"
                style={{ width: "100%" }}
                size="large"
                value={selectedDepartment}
                onChange={(value) => {
                  setSelectedDepartment(value);
                  const deptEmps = employees.filter(e => e.department === value);
                  setSelectedEmployee(deptEmps[0]?.id || null);
                }}
                options={activeDepartments.map(dept => ({
                  label: `${dept.name} (${dept.employeeCount} employees)`,
                  value: dept.name,
                }))}
              />
            </Col>
            <Col span={12}>
              <SelectInput
                label="Employee"
                style={{ width: "100%" }}
                size="large"
                value={selectedEmployee}
                onChange={setSelectedEmployee}
                options={departmentEmployees.map(emp => ({
                  label: `${emp.firstname} ${emp.lastname} - ${emp.designation || emp.role}`,
                  value: emp.id,
                }))}
              />
            </Col>
          </Row>
        </Card>
      )}

      <Row gutter={20}>
        {mode === MODE.DEPARTMENT && (
          <Col span={6}>
            <Card style={styles.departmentCard} bodyStyle={{ padding: 16 }}>
              <InputField
                placeholder="Search departments..."
                prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
                style={{ marginBottom: 16 }}
                value={searchDepartments}
                onChange={(e) => setSearchDepartments(e.target.value)}
                allowClear
              />
              {filteredDepartments.map(dept => (
                <div
                  key={dept.name}
                  style={styles.departmentItem(selectedDepartment === dept.name)}
                  onClick={() => setSelectedDepartment(dept.name)}
                >
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <ApartmentOutlined />
                      <Text strong style={{ color: "inherit" }}>
                        {dept.name}
                      </Text>
                    </div>
                    <Text
                      style={{
                        fontSize: 12,
                        color: selectedDepartment === dept.name ? "rgba(255,255,255,0.8)" : "#8c8c8c",
                      }}
                    >
                      {dept.employeeCount} employees
                    </Text>
                  </div>
                  <Badge
                    count={(departmentRoutes[dept.name] || []).length}
                    style={{
                      backgroundColor: selectedDepartment === dept.name ? "rgba(255,255,255,0.25)" : "#f0f0f0",
                      color: selectedDepartment === dept.name ? "#fff" : "#595959",
                      boxShadow: "none",
                    }}
                  />
                </div>
              ))}
            </Card>
          </Col>
        )}

        <Col span={mode === MODE.DEPARTMENT ? 18 : 24}>
          <Card style={styles.mainCard} bodyStyle={{ padding: 0 }}>
            {mode === MODE.INDIVIDUAL && currentEmployee && (
              <div style={styles.employeeCard}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Space size={16}>
                    <Avatar size={48} src={currentEmployee.image} style={{ background: "#1677ff" }}>
                      {currentEmployee.firstname?.charAt(0)}
                    </Avatar>
                    <div>
                      <Title level={5} style={{ margin: 0 }}>
                        {formatEmployeeName(currentEmployee)}
                      </Title>
                      <Text type="secondary">
                        {currentEmployee.designation || currentEmployee.role} • {currentEmployee.department}
                      </Text>
                    </div>
                  </Space>
                  <Space>
                    <Tag color="blue">{getInheritedRoutes(selectedEmployee).length} Inherited</Tag>
                    <Tag color="gold">{(employeeCustomRoutes[selectedEmployee] || []).length} Custom</Tag>
                  </Space>
                </div>
              </div>
            )}

            <div style={{ padding: "20px 24px", borderBottom: "1px solid #f0f0f0" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <div>
                  <Title level={5} style={{ margin: 0 }}>
                    {mode === MODE.DEPARTMENT ? `${currentDepartment?.name} Department Routes` : "Route Permissions"}
                  </Title>
                  <Text type="secondary">{assignedCount} routes assigned</Text>
                </div>
                <Space>
                  <InputField
                    placeholder="Search routes..."
                    prefix={<SearchOutlined />}
                    style={{ width: 250 }}
                    value={searchRoutes}
                    onChange={(e) => setSearchRoutes(e.target.value)}
                    allowClear
                  />
                  {mode === MODE.INDIVIDUAL && (
                    <SelectInput
                      style={{ width: 150 }}
                      value={routeFilter}
                      onChange={setRouteFilter}
                      options={[
                        { label: "All Routes", value: ROUTE_FILTER.ALL },
                        { label: "Inherited Only", value: ROUTE_FILTER.INHERITED },
                        { label: "Custom Only", value: ROUTE_FILTER.CUSTOM },
                      ]}
                    />
                  )}
                </Space>
              </div>
            </div>

            <div style={{ padding: "16px 24px", maxHeight: 500, overflowY: "auto" }}>
              <Row gutter={[16, 16]}>
                {filteredRoutes.map((menu, index) => {
                  const mainStatus = getRouteStatus(menu, null);
                  const hasSubfields = menu.Subfields?.length > 0;

                  return (
                    <Col xs={24} sm={12} md={12} lg={6} key={`route-${menu.name}-${index}`}>
                      <div style={styles.routeItem(mainStatus.checked, mainStatus.type)}>
                        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <Checkbox
                                checked={mainStatus.checked}
                                onChange={() => {
                                  const handler = mode === MODE.DEPARTMENT ? handleDepartmentRouteToggle : handleEmployeeRouteToggle;
                                  handler(menu, null);
                                }}
                              />
                              <Text strong style={{ fontSize: 14 }}>{menu.name}</Text>
                            </div>
                          </div>

                          {mode === MODE.INDIVIDUAL && mainStatus.checked && mainStatus.type !== ROUTE_TYPE.NONE && (
                            <Tag
                              color={mainStatus.type === ROUTE_TYPE.INHERITED ? "blue" : "gold"}
                              style={{ fontSize: 10, width: "fit-content", marginLeft: 28, marginTop: 2 }}
                            >
                              {mainStatus.type === ROUTE_TYPE.INHERITED ? "Inherited" : "Custom"}
                            </Tag>
                          )}

                          {hasSubfields && (
                            <div style={{ marginLeft: 28, marginTop: 4 }}>
                              {menu.Subfields.map((subfield, subIndex) => {
                                const subStatus = getRouteStatus(menu, subfield);
                                return (
                                  <div key={`sub-${menu.name}-${subfield.displayName}-${subIndex}`} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                                    <Checkbox
                                      checked={subStatus.checked}
                                      onChange={() => {
                                        const handler = mode === MODE.DEPARTMENT ? handleDepartmentRouteToggle : handleEmployeeRouteToggle;
                                        handler(menu, subfield);
                                      }}
                                    />
                                    <div style={{ flex: 1 }}>
                                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                        <Text style={{ fontSize: 12 }}>{subfield.displayName}</Text>
                                        {mode === MODE.INDIVIDUAL && subStatus.checked && (
                                          <Tag
                                            color={subStatus.type === ROUTE_TYPE.INHERITED ? "blue" : "gold"}
                                            style={{ fontSize: 9, padding: "0 4px", margin: 0 }}
                                          >
                                            {subStatus.type === ROUTE_TYPE.INHERITED ? "Inherited" : "Custom"}
                                          </Tag>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    </Col>
                  );
                })}
              </Row>
            </div>

            <div style={styles.footer}>
              <Space>
                <div style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: hasUnsavedChanges ? "#faad14" : "#52c41a"
                }} />
                <Text type="secondary">
                  {mode === MODE.DEPARTMENT
                    ? `Changes will apply to all ${currentDepartment?.employeeCount} employees in ${currentDepartment?.name} department`
                    : `Custom overrides will apply only to ${formatEmployeeName(currentEmployee)}`}
                </Text>
                {hasUnsavedChanges && (
                  <Tag color="warning">Unsaved changes</Tag>
                )}
              </Space>
              <Space>
                <AntdButton
                  size="large"
                  label="Cancel"
                  disabled={saving}
                />
                <AntdButton
                  type="primary"
                  size="large"
                  icon={<SaveOutlined />}
                  onClick={handleSave}
                  loading={saving}
                  disabled={!hasUnsavedChanges || saving}
                  label={mode === MODE.DEPARTMENT ? "Save Changes" : "Apply Changes"}
                />
              </Space>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

// Styles
const styles = {
  container: {
    minHeight: "100vh",
    background: "#f5f5f5",
    padding: "24px",
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 24,
  },
  mainCard: {
    borderRadius: 12,
    boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
    border: "none",
  },
  departmentCard: {
    borderRadius: 12,
    boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
    height: "100%",
    border: "none",
  },
  departmentItem: (isSelected) => ({
    padding: "12px 16px",
    marginBottom: 8,
    borderRadius: 8,
    cursor: "pointer",
    background: isSelected ? "#1677ff" : "#fff",
    color: isSelected ? "#fff" : "#333",
    border: "none",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    transition: "all 0.2s",
  }),
  routeItem: (isChecked, type) => ({
    padding: "12px",
    height: "100%",
    borderRadius: 10,
    background: isChecked
      ? type === ROUTE_TYPE.CUSTOM ? "#fffbe6" : "#e6f4ff"
      : "#fff",
    border: `1px solid ${isChecked
      ? type === ROUTE_TYPE.CUSTOM ? "#ffe58f" : "#91d5ff"
      : "#f0f0f0"
      }`,
    transition: "all 0.2s",
  }),
  employeeCard: {
    padding: 20,
    marginBottom: 0,
    background: "#fafafa",
    borderRadius: 0,
    borderBottom: "1px solid #f0f0f0",
  },
  footer: {
    padding: "16px 24px",
    background: "#fafafa",
    borderTop: "1px solid #f0f0f0",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    borderRadius: "0 0 12px 12px",
  },
};